import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ImageUp, ChevronRight } from "lucide-react";
import { PAY_STATUS_LABEL, SELECT_CLS, today } from "@/components/SalesShared";

export default function TransferProofs() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [branches, setBranches] = useState([]);
  const [fl, setFl] = useState({ start: today(), end: today(), branch_id: "", proof: "" });
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const p = Object.fromEntries(Object.entries({ start_date: fl.start, end_date: fl.end, branch_id: fl.branch_id, proof: fl.proof }).filter(([, v]) => v));
    api.get("/transfer-proofs", { params: p }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [fl]);
  const set = (k) => (e) => setFl((p) => ({ ...p, [k]: e.target.value }));
  const total = (rows || []).reduce((t, r) => t + (r.transfer_total || 0), 0);
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}><ImageUp className="h-6 w-6 text-brand" /> Bukti Transfer</h2>
        <p className="text-sm text-muted-foreground">Transaksi Transfer dari semua Pemohon. Klik baris untuk melihat detail & bukti.</p>
      </div>
      <Card className="p-4 grid grid-cols-2 lg:grid-cols-4 gap-3 items-end" data-testid="tfp-filters">
        <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" value={fl.start} onChange={set("start")} className="mt-1 h-9" data-testid="tfp-start" /></div>
        <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" value={fl.end} onChange={set("end")} className="mt-1 h-9" data-testid="tfp-end" /></div>
        <div><Label className="text-xs">Cabang</Label>
          <select value={fl.branch_id} onChange={set("branch_id")} className={`${SELECT_CLS} mt-1 h-9`} data-testid="tfp-branch">
            <option value="">Semua Cabang</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select></div>
        <div><Label className="text-xs">Status Bukti</Label>
          <select value={fl.proof} onChange={set("proof")} className={`${SELECT_CLS} mt-1 h-9`} data-testid="tfp-proof">
            <option value="">Semua</option><option value="has">Sudah Upload</option><option value="none">Belum Upload</option>
          </select></div>
      </Card>
      <Card className="p-0 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b">
          <h3 className="font-semibold" style={{ fontFamily: "Outfit" }} data-testid="tfp-count">Transaksi Transfer ({rows?.length || 0})</h3>
          <p className="text-sm">Total Transfer <span className="font-mono font-semibold" data-testid="tfp-total">{formatRupiah(total)}</span></p>
        </div>
        {rows === null ? <div className="p-6"><div className="h-4 rounded-md skeleton-shimmer" /></div> : rows.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground" data-testid="tfp-empty">Tidak ada transaksi transfer pada filter ini.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tfp-table">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground">
                <tr><th className="text-left px-4 py-2.5">Tanggal</th><th className="text-left px-4 py-2.5">Nota</th><th className="text-left px-4 py-2.5">Pemohon</th><th className="text-left px-4 py-2.5">Pelanggan</th>
                  <th className="text-left px-4 py-2.5">Cara Bayar</th><th className="text-right px-4 py-2.5">Nominal Transfer</th><th className="text-left px-4 py-2.5">Bukti</th><th className="px-4 py-2.5"></th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => navigate(`/transfer-proofs/${r.id}`)} className="border-t cursor-pointer hover:bg-secondary/60" data-testid={`tfp-row-${r.id}`}>
                    <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(r.date)}</td>
                    <td className="px-4 py-2.5 font-mono">{r.nota_number}</td>
                    <td className="px-4 py-2.5">{r.owner_name}<span className="block text-xs text-muted-foreground">{r.branch_name}</span></td>
                    <td className="px-4 py-2.5">{r.customer_name}</td>
                    <td className="px-4 py-2.5">{PAY_STATUS_LABEL[r.pay_status]}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{formatRupiah(r.transfer_total)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-md font-semibold ${r.proof_count ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid={`tfp-proof-status-${r.id}`}>{r.proof_count ? `Sudah (${r.proof_count})` : "Belum"}</span>
                    </td>
                    <td className="px-4 py-2.5"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
